import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import BilgiDugmesi from '../components/BilgiDugmesi.js';

/**
 * `/kilavuz` — panel içi kullanma kılavuzu.
 *
 * `docs/kilavuz.md`nin panel karşılığıdır: ekranların ne işe yaradığı, tipik iş
 * akışı, panel kabuğu, küçük sözlük ve sorun giderme. İçerik STATİKTİR — hiçbir
 * uca istek atmaz; kılavuzun kendisi hiçbir koşulda "yüklenemedi" diyemez.
 *
 * NEDEN PANEL İÇİNDE: kılavuz depoda `docs/` altında yaşıyordu ama panel
 * kullanıcısının depoya erişimi olmayabilir — "ekranların ne işe yaradığını
 * anlayamadım" geri bildirimi, dokümanın KULLANICININ OLDUĞU YERDE durmadığının
 * kanıtıydı. Ekran başlıklarındaki (i) balonları tek cümlelik özet verir; bu
 * sayfa aynı bilginin uzun hâlidir.
 *
 * BAKIM DEĞİŞMEZİ: ekran kartlarındaki metinler `docs/kilavuz.md` §5 ile aynı
 * kaynaktan türetilmiştir — bir ekranın davranışı değişirse ikisi birlikte
 * güncellenir (kılavuzun dipnotu da bunu söyler).
 */

/** Ekran rehberi tek veri kaynağından çizilir — kart şablonu kopyalanmaz. */
interface EkranTanimi {
  yol: string;
  ad: string;
  /** Rozet: sayfanın kapsamı (proje mi org mu, ana sayfa mı). */
  kapsam?: string;
  neIseYarar: ReactNode;
  neYapilir?: ReactNode;
  /** "Boşsa neden boş" / bilinçli sınır notu. */
  not?: ReactNode;
}

const EKRANLAR: EkranTanimi[] = [
  {
    yol: '/bulgular',
    ad: 'Bulgular',
    kapsam: 'ana sayfa',
    neIseYarar:
      'İnsight motorunun ürettiği, önceliğe göre sıralı iş listesi. En üstteki kart TL etkisi en yüksek olandır; etki her zaman aralık olarak verilir ("12–18 bin TL/ay") çünkü kesin sayı dürüst olmazdı.',
    neYapilir:
      'Kartları durum sekmeleriyle süzersiniz (Yeni / Gördüm / Uygulandı / Yoksayıldı); kart üstünde Gördüm / Uyguladım / Yoksay; "örnek oturumlar" linki sizi o hatayı yaşayan gerçek oturumlara götürür. Listeyi CSV/JSONL olarak dışa aktarabilirsiniz (§95).',
    not: 'Boşsa: motor henüz koşmadı ya da yeterli trafik yok — önce trafik akıtın.',
  },
  {
    yol: '/',
    ad: 'Genel Bakış',
    neIseYarar:
      'Son 30 günün özeti — oturum, kullanıcı, dönüşüm, gelir, aktif bulgu sayısı; her metriğin yanında önceki 30 güne göre Δ rozeti (önceki dönem verisi yetersizse rozet çizilmez); eğilim grafiği; kanal ve cihaz kırılımı. Salt okuma ekranıdır.',
    not: 'Boşsa: SDK henüz veri göndermiyor demektir; ekran kurulum çağrısı gösterir.',
  },
  {
    yol: '/deneyler',
    ad: 'Deneyler',
    neIseYarar:
      'A/B deneyleri. Varyantları ziyaretçilere SDK dağıtır; sonuçlar always-valid sıralı testle izlenir — sonuca istediğiniz an bakabilirsiniz, test bozulmaz. Panel asla erken "kazanan" ilan etmez. Deneyin HANGİ SAYFADA koştuğunu (test yüzeyi) yazarsanız motor, atanan oturumların o sayfayı görüp görmediğini sayar; hiçbiri görmediyse panel "deney sitede uygulanmamış olabilir" diye uyarır.',
    neYapilir:
      'Yeni deney (ad, hipotez, test yüzeyi, varyantlar — her birinin adı, trafik ağırlığı ve ne gösterdiği — ve hedef) → Başlat / Durdur; kartta varyant başına oturum, dönüşüm ve lift aralığı okunur. Karttaki "Detay" düğmesi deneyin kendi sayfasını açar: oluşturma/başlangıç/bitiş tarihleri, geçen süre, motorun uygulayacağı otomatik kapanış tarihi ve uygulama durumu oradadır. İki AYRI SAYFAYI karşılaştırmak isterseniz (split-URL) formda o anahtarı açıp her varyanta bir adres verirsiniz — yönlendirmeyi ürün YAPMAZ, sunucunuz yapar ve detay sayfası size tam talimatı gösterir.',
  },
  {
    yol: '/funnel',
    ad: 'Funnel',
    neIseYarar:
      'Dönüşüm hunisinin Sankey çizimi. Adımları siz tanımlamazsınız — huni, otomatik keşfedilen hedeften geriye doğru türetilir. Bir adıma tıklayınca o adımı görüp sonrakine GEÇMEYEN oturumlar listelenir.',
    neYapilir:
      'Segment seçici: hazır kırılımlar (cihaz/kanal) ve kendi tanımladığınız kayıtlı segmentler ("Mobil + Organik" gibi); trafik kaynaklarını aç/kapa; adım kutusundaki "önceki %X" satırı önceki 14 günle kıyastır; bir adıma tıklayınca terk eden örnek oturumlar listelenir.',
  },
  {
    yol: '/hedefler',
    ad: 'Hedefler',
    neIseYarar:
      '"Dönüşüm" saydığınız şeyi sistem kendisi keşfeder (ödeme onayı, form gönderimi…), adayları güven skoru ve dört kanıt bileşeniyle önünüze koyar.',
    neYapilir:
      'Tek karar sizindir — adayı Onayla ya da Reddet. Onaylı hedef; funnel’ın, deneylerin ve gelir hesabının temelidir.',
  },
  {
    yol: '/oturumlar',
    ad: 'Oturumlar',
    neIseYarar:
      'Tekil ziyaretçi oturumları. Satıra tıklayınca olay zaman çizelgesi (sayfa → tıklama → hata → …) açılır; "Kaydı oynat" oturumun ekran kaydını (replay) oynatır.',
    neYapilir:
      'Hata / Rage click / Terk / Dönüşüm sekmeleriyle süzün; kayıtlı segment seçin (sekmeyle birleşir); "Dışa aktar" gördüğünüz listeyi CSV/JSONL indirir; bulgu kartından "örnek oturum" linkiyle gelirseniz oturum otomatik seçilir.',
  },
  {
    yol: '/heatmap',
    ad: 'Isı Haritası',
    neIseYarar:
      'Seçtiğiniz sayfa deseni için tıklama yoğunluğu ve scroll erişimi, sitenizin canlı görüntüsü üzerine bindirilir; altta "en çok tıklanan öğeler" listesi.',
    neYapilir: 'Sayfa deseni + cihaz (masaüstü/mobil) + mod (tıklama/scroll) seçin.',
    not: 'Boşsa: o desen/cihaz için henüz veri birikmedi.',
  },
  {
    yol: '/seo',
    ad: 'SEO',
    neIseYarar:
      'Tarayıcı sitenizi kendisi gezer ve kullanıcı daha hiç gelmeden görülebilen kusurları bulur: eksik meta, kırık iç link, beklenmedik noindex… 100 üzerinden site skoru + ceza puanına göre sıralı sorun tablosu.',
    neYapilir: 'Yeniden tara; sorun satırına tıklayıp etkilenen sayfaları görün.',
    not: 'Bilinçli sınır: sayfa hızı burada ölçülmez — hızın tek kaynağı gerçek kullanıcı ölçümüdür (RUM).',
  },
  {
    yol: '/uzun-vadeli',
    ad: 'Uzun Vadeli',
    neIseYarar:
      '12–24 aylık yönetici raporu — yıldan yıla değişim, aylık trend, kanal karması evrimi, en iyi/kötü sayfalar, funnel değişimi. Rapor gece hazırlanır; bu sayfa hazır raporu okur, canlı ağır sorgu çalıştırmaz.',
    neYapilir:
      'Raporu hazırla / Yenile (kuyruğa atar; "Rapor hazırlanıyor" görürseniz veri yok değil, sıradadır).',
  },
  {
    yol: '/retention',
    ad: 'Geri Dönüş',
    neIseYarar:
      'Haftalık kohort ızgarası: her satır o hafta ilk kez görülen ziyaretçiler, hücreler +k hafta sonra geri gelenlerin yüzdesi. Ziyaretçiler anonimdir — hücre sayıdır, kişi listesi açılmaz; bitmemiş haftalar "—" ile boş kalır.',
    neYapilir:
      'Kohort boyutlarına ve yüzde yoğunluğuna bakılır; her kohortun ISO hafta etiketinin altında takvim aralığı yazar ("10 – 16 Ağustos"), hücrenin üzerine gelince ham kesir ve o hücrenin haftası görünür. Izgarayı dışa aktarabilirsiniz (§95).',
    not: 'Boşsa: kohortlar SDK veri göndermeye başladıktan sonra haftadan haftaya birikir.',
  },
  {
    yol: '/ayarlar',
    ad: 'Ayarlar',
    kapsam: 'proje',
    neIseYarar:
      'Seçili projenin yapılandırması: e-posta raporu aboneleri (çift onaylı — eklenen adres, mektuptaki onaya tıklayana kadar rapor almaz), proje kimliği + SDK etiketi + API anahtarı (yalnız owner), üyeler ve davetler, veri hakları (KVKK/GDPR dışa aktarma-silme) ve ölçülmüş maliyet.',
  },
  {
    yol: '/organizasyon',
    ad: 'Organizasyon',
    kapsam: 'org',
    neIseYarar: 'Org üyeleri/rolleri/davetleri ve kurumsal giriş (SSO/OIDC) yapılandırması.',
    not: 'Ayrım önemli: buradaki üyelik organizasyon üyeliğidir; birini org’dan çıkarmak proje erişimini otomatik kaldırmaz. Proje üyeliği Ayarlar’dadır.',
  },
  {
    yol: '/faturalama',
    ad: 'Faturalama',
    kapsam: 'org',
    neIseYarar: 'Plan kataloğu, abonelik durumu, fatura geçmişi.',
    not: 'Bilinçli davranış: panel abonelik durumunu kendi ilerletmez — "Planı yükselt" sizi ödeme akışına götürür, durum yalnız ödeme sağlayıcısının webhook’u gelince değişir. İptal dönem sonunda yürürlüğe girer; veriler silinmez.',
  },
  {
    yol: '/webhooks',
    ad: 'Webhook’lar',
    kapsam: 'org',
    neIseYarar:
      'Paneli açmadan haberdar olmak: seçtiğiniz olaylar (yeni bulgu, kota eşiği…) verdiğiniz adrese imzalı POST olarak gider; başarısız teslimat geri çekilme merdiveniyle yeniden denenir.',
    neYapilir:
      'Yeni uç (URL + olay seçimi) → imza sırrı BİR KEZ gösterilir, hemen kaydedin; uç kartında teslimat geçmişi; yalnız "ölü" satırda Yeniden dene.',
    not: 'Bu sayfa org’da en az admin rolü ister.',
  },
  {
    yol: '/kurulum',
    ad: 'Kuruluma başla',
    neIseYarar:
      'İlk giriş sihirbazı: organizasyon → ilk proje → SDK etiketi. Org’u olmayan kullanıcı hangi adrese giderse gitsin buraya yönlendirilir; üç adım bitince panel açılır.',
  },
  {
    yol: '/hesap',
    ad: 'Hesap',
    neIseYarar:
      'Kendi hesabınız: ad, parola değişikliği, açık oturumlar (şüpheli cihazın oturumunu kapatma), hesabı kalıcı silme. E-posta adresi bilinçli olarak değiştirilemez. Parola değişince DİĞER cihazlardaki oturumlar kapanır, bulunduğunuz cihaz açık kalır.',
  },
];

const SOZLUK: [string, string][] = [
  ['Bulgu (insight)', 'Motorun ürettiği, TL etkisi + reçetesi olan iş maddesi'],
  ['Hedef keşfi', '"Dönüşüm"ün ne olduğunu sistemin kanıtla önermesi; siz yalnız onaylarsınız'],
  [
    'Always-valid test',
    'Sonuca istediğiniz an bakabildiğiniz A/B istatistiği — "erken bakıp bozma" riski yok',
  ],
  ['RUM', 'Gerçek kullanıcı ölçümü; sayfa hızının tek kaynağı'],
  ['Rage click', 'Aynı yere art arda öfkeli tıklama — kırık etkileşim sinyali'],
  ['Çift onay (double opt-in)', 'E-posta abonesi, mektuptaki onaya tıklamadan rapor almaz'],
  ['Kota', 'Aylık olay tavanı; aşımda veri kesilmez, yalnız uyarılırsınız'],
];

const SORUNLAR: [string, ReactNode][] = [
  [
    'Panel tümüyle boş',
    <>
      SDK takılı değil ya da hiç trafik yok — SDK etiketi <Link to="/ayarlar" className="text-brand hover:underline">Ayarlar → Proje</Link> bölümündedir.
    </>,
  ],
  ['Bulgular boş ama Genel Bakış dolu', 'İnsight motoru periyodiktir — birkaç dakika bekleyin.'],
  [
    'Doğrulama/davet mektubu gelmiyor',
    'Geliştirme kurulumunda mektup gönderilmez: depodaki var/outbox/ klasöründeki .eml dosyasını açın.',
  ],
  [
    'Webhook’lar sayfası 403 veriyor',
    'Org rolünüz "member" — en az "admin" gerekir; bir owner, Organizasyon sayfasından yükseltmeli.',
  ],
  ['Isı haritası "veri yok" diyor', 'O sayfa deseni/cihaz için henüz olay birikmedi.'],
  [
    '"Rapor hazırlanıyor" (Uzun Vadeli)',
    'Kuyrukta — veri kaybı değil; gece penceresini ya da worker’ı bekliyor.',
  ],
  [
    'Üst çubukta kırmızı sağlık noktası',
    'Ingest API’ye ulaşılamıyor — servislerin çalıştığından emin olun.',
  ],
];

/** Bölüm kartı — beyaz kutu + 18px başlık (panelin kart dili). */
function Bolum({ baslik, children }: { baslik: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[#e5e7eb] bg-white p-6">
      <h2 className="mb-4 font-grotesk text-[15px] font-semibold text-[#111827]">{baslik}</h2>
      {children}
    </section>
  );
}

export default function Kilavuz() {
  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="kilavuz-sayfasi">
      {/* başlık şeridi */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-brand" aria-hidden="true" />
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Kılavuz</h1>
          <BilgiDugmesi testId="kilavuz-bilgi">
            Panelin kullanma kılavuzu: ekranların ne işe yaradığı, tipik iş akışı ve sorun
            giderme. Her ekranın başlığındaki (i) simgesi aynı bilginin tek cümlelik özetidir.
          </BilgiDugmesi>
        </div>
        <span className="text-[12px] text-[#9ca3af]">salt okuma · içerik panelle birlikte güncellenir</span>
      </div>

      <div className="mx-auto max-w-[880px] space-y-6 px-6 py-8">
        <Bolum baslik="DXP ne yapar?">
          <div className="space-y-3 text-[13.5px] leading-relaxed text-[#374151]">
            <p>
              Sitenize <strong>tek satırlık bir SDK etiketi</strong> eklersiniz. SDK, ziyaretçi
              davranışını (sayfa görüntüleme, tıklama, rage click, JS hatası, form hatası, web
              vitals…) toplar — <strong>kişisel veri asla gitmez</strong>: input değerleri,
              e-posta ve kart bilgisi daha tarayıcıdan çıkmadan maskelenir.
            </p>
            <p>
              Sunucudaki <strong>insight motoru</strong> bu ham veriden <strong>bulgular</strong>{' '}
              üretir: "Ödeme sayfasındaki form mobilde hata veriyor, tahmini kaybınız ayda 12–18
              bin TL" gibi, para etkisiyle sıralanmış, reçeteli iş maddeleri. Panelin geri kalanı
              bu teşhisi destekleyen araçlardır: funnel, ısı haritası, oturum kaydı, A/B
              deneyleri, SEO taraması, uzun vadeli rapor.
            </p>
            <p className="font-medium text-[#111827]">
              Kısacası: veriyi siz yorumlamazsınız; sistem yorumlar, siz en üstteki bulgudan
              başlayıp uygularsınız.
            </p>
          </div>
        </Bolum>

        <Bolum baslik="Tipik akış — ilk 10 dakika">
          <ol className="list-decimal space-y-2 pl-5 text-[13.5px] leading-relaxed text-[#374151]">
            <li>
              Organizasyonunuz yoksa panel sizi otomatik olarak{' '}
              <Link to="/kurulum" className="text-brand hover:underline">
                Kuruluma başla
              </Link>{' '}
              sihirbazına atar: org adı → ilk proje → SDK etiketi.
            </li>
            <li>
              SDK etiketini ölçmek istediğiniz sitenin <code className="rounded bg-[#f3f4f6] px-1">&lt;head&gt;</code>
              &apos;ine ekleyin (etiket{' '}
              <Link to="/ayarlar" className="text-brand hover:underline">
                Ayarlar → Proje
              </Link>{' '}
              bölümünde her zaman durur).
            </li>
            <li>Trafik akıtın; birkaç dakika içinde Genel Bakış dolar. İnsight motoru periyodik koştuğu için Bulgular biraz daha geç dolabilir.</li>
            <li>
              <Link to="/bulgular" className="text-brand hover:underline">
                Bulgular
              </Link>
              &apos;dan başlayın: en üstteki kart, TL etkisi en yüksek iştir. Reçetesini uygulayın,
              durumunu "Uyguladım" yapın, etkisini Funnel/Genel Bakış&apos;tan izleyin.
            </li>
          </ol>
        </Bolum>

        <Bolum baslik="Panel kabuğu (her ekranda görünen çerçeve)">
          <ul className="list-disc space-y-2 pl-5 text-[13.5px] leading-relaxed text-[#374151]">
            <li>
              <strong>Proje seçici</strong> (sol üst) — organizasyon ve proje değiştirme; arşivli
              projeler ayrı "Arşiv" başlığında; en altta "Yeni proje".
            </li>
            <li>
              <strong>Arama</strong> (orta, ⌘K) — komut paleti: sayfa, oturum veya bulgu arayıp
              klavyeyle gidersiniz.
            </li>
            <li>
              <strong>Sağlık noktası</strong> — yeşil: çalışıyor · sarı: kısmi kesinti · kırmızı:
              API erişilemiyor (60 saniyede bir yoklar).
            </li>
            <li>
              <strong>Zil</strong> — organizasyon bildirimleri + okunmamış rozeti.{' '}
              <strong>Avatar</strong> — Hesabım, Çıkış.
            </li>
            <li>
              <strong>Kota şeridi</strong> — aylık olay kotasının %80&apos;inde sarı, %100&apos;ünde
              kırmızı şerit belirir. Hiçbir şeyi engellemez; veri toplanmaya devam eder, yalnız
              haber verir.
            </li>
          </ul>
        </Bolum>

        <Bolum baslik="Ekran ekran rehber">
          <div className="space-y-4">
            {EKRANLAR.map((e) => (
              <div
                key={e.yol}
                data-testid="kilavuz-ekran"
                className="rounded-lg border border-[#e5e7eb] bg-[#fafafa] p-4"
              >
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <Link
                    to={e.yol}
                    className="font-grotesk text-[14px] font-semibold text-[#111827] hover:text-brand"
                  >
                    {e.ad}
                  </Link>
                  <code className="rounded bg-[#fdeee2] px-1.5 py-0.5 text-[11px] text-brand">
                    {e.yol}
                  </code>
                  {e.kapsam !== undefined ? (
                    <span className="rounded-full border border-[#e5e7eb] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#6b7280]">
                      {e.kapsam}
                    </span>
                  ) : null}
                </div>
                <p className="text-[13px] leading-relaxed text-[#374151]">{e.neIseYarar}</p>
                {e.neYapilir !== undefined ? (
                  <p className="mt-1.5 text-[13px] leading-relaxed text-[#374151]">
                    <span className="font-medium text-[#111827]">Burada ne yapılır: </span>
                    {e.neYapilir}
                  </p>
                ) : null}
                {e.not !== undefined ? (
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#6b7280]">{e.not}</p>
                ) : null}
              </div>
            ))}
          </div>
        </Bolum>

        <Bolum baslik="Küçük sözlük">
          <dl className="space-y-2.5">
            {SOZLUK.map(([terim, anlam]) => (
              <div key={terim} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="w-56 shrink-0 text-[13px] font-medium text-[#111827]">{terim}</dt>
                <dd className="m-0 text-[13px] text-[#374151]">{anlam}</dd>
              </div>
            ))}
          </dl>
        </Bolum>

        <Bolum baslik="Sorun giderme">
          <dl className="space-y-2.5">
            {SORUNLAR.map(([belirti, cozum], i) => (
              <div key={i} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="w-56 shrink-0 text-[13px] font-medium text-[#111827]">{belirti}</dt>
                <dd className="m-0 text-[13px] text-[#374151]">{cozum}</dd>
              </div>
            ))}
          </dl>
        </Bolum>

        <p className="pb-4 text-[12px] text-[#9ca3af]">
          Ekran başlıklarındaki (i) balonları bu kılavuzla aynı kaynaktan türetilmiştir; bir
          ekranın davranışı değişirse ikisi birlikte güncellenir. Geliştirici kopyası:{' '}
          <code>docs/kilavuz.md</code>
        </p>
      </div>
    </div>
  );
}
